'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import Link from '@eliadmin/core/src/components/link';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { GoArrowRight } from 'react-icons/go';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { PropsWithChildren } from 'react';

type CategoryCardProps = {
  category?: CategoriesEntity;
  href?: string;
  noLink?: boolean;
  className?: { root?: string; content?: string };
};

export function CategoryCard({
  category,
  href,
  noLink = false,
  className,
  children,
}: PropsWithChildren<CategoryCardProps>) {
  if (category) {
    return (
      <Link
        prefetch={false}
        href={ROUTES.CATEGORY({ slug: category.slug })}
        className="group relative col-span-1 flex h-[350px] w-full items-end justify-start overflow-hidden p-4 sm:h-[380px] sm:p-5 lg:h-[500px] lg:p-6"
      >
        <GoArrowRight
          size={32}
          className={cn(
            'absolute right-4 top-4 z-10 text-white sm:left-5 sm:top-5 lg:left-6 lg:top-6',
            'transition-all group-hover:translate-x-2',
          )}
        />
        <h4 className="relative z-10 font-oswald text-2xl font-bold uppercase text-white sm:text-3xl lg:text-4xl xl:text-[40px]">
          {category.name}
        </h4>
        <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-category-card__overlay')} />
        <Image
          src={category?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(category?.image, category?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
      </Link>
    );
  }

  if (noLink) {
    return (
      <Card
        asChild
        className={cn('h-[56px] hover:bg-secondary/80', 'ea-category-card', className?.root)}
        variant="outlined"
      >
        <CardContent
          className={cn(
            'line-clamp-2 flex h-full w-full flex-row items-center gap-4 text-ellipsis p-3',
            'ea-category-card__content',
            className?.content,
          )}
        >
          {children}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      asChild
      className={cn('h-[56px] hover:bg-secondary/80', 'ea-category-card', className?.root)}
      variant="outlined"
    >
      <Link href={href ?? '#'} className={cn('w-full', 'ea-category-card__link', className?.root)} prefetch={false}>
        <CardContent
          className={cn(
            'line-clamp-2 flex h-full w-full flex-row items-center gap-4 text-ellipsis p-3',
            'ea-category-card__content',
            className?.content,
          )}
        >
          {children}
        </CardContent>
      </Link>
    </Card>
  );
}

type CategoryCardImageProps = {
  url: string;
  alt?: string;
  className?: {
    root?: string;
    image?: string;
  };
};

export function CategoryCardImage(props: CategoryCardImageProps) {
  return (
    <div className={cn('flex-shrink-0', 'ea-category-card-image__root', props.className?.root)}>
      <Image
        alt={resolveImageAlt(props, 'category-image')}
        src={props.url}
        width="0"
        height="0"
        sizes={buildImageSizes([{ size: '32px' }])}
        className={cn(
          'h-8 w-8 object-contain',
          'ea-category-card-image__image',
          props.className?.image,
        )}
      />
    </div>
  );
}

export function CategoryCardName(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('text-left text-sm', 'ea-category-card-name', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}
